// AppInstanceReadService.js

import { collection, onSnapshot } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

import { normalizeAppInstanceRecord, getAppInstanceDisplayTitle } from './appInstanceModel';

const sharedAppInstanceSubscriptions = new Map();

function normalizeBaseKey(baseKey) {
  return String(baseKey || '').trim();
}

function buildBaseSubscriptionKey(baseKey) {
  return `baseKey=${normalizeBaseKey(baseKey) || '*'}`;
}

function ensureSharedAppInstanceSubscription(baseKey) {
  const cleanedBaseKey = normalizeBaseKey(baseKey);
  const scopeKey = buildBaseSubscriptionKey(cleanedBaseKey);
  const existing = sharedAppInstanceSubscriptions.get(scopeKey);
  if (existing) return existing;

  const entry = {
    listeners: new Set(),
    rows: [],
    scopeKey,
    unsubscribe: null,
    baseKey: cleanedBaseKey,
  };

  if (!cleanedBaseKey) {
    sharedAppInstanceSubscriptions.set(scopeKey, entry);
    return entry;
  }

  const appInstancesRef = collection(
    db,
    collections.bases,
    cleanedBaseKey,
    collections.appInstances,
  );

  entry.unsubscribe = onSnapshot(
    appInstancesRef,
    (snapshot) => {
      entry.rows = snapshot.docs
        .map((docSnap) => normalizeAppInstanceRecord(docSnap.data() || {}, docSnap.id))
        .filter(Boolean)
        .sort((left, right) => getAppInstanceDisplayTitle(left).localeCompare(getAppInstanceDisplayTitle(right)));

      entry.listeners.forEach((listener) => {
        try {
          listener(entry.rows);
        } catch (error) {
          console.error('[AppInstanceReadService] shared listener error:', error);
        }
      });
    },
    (error) => {
      console.error('[AppInstanceReadService] subscribeToBaseAppInstances error:', error);
      entry.rows = [];
      entry.listeners.forEach((listener) => listener([]));
    },
  );

  sharedAppInstanceSubscriptions.set(scopeKey, entry);
  return entry;
}

export function subscribeToBaseAppInstances(baseKey, callback) {
  if (typeof callback !== 'function') {
    return () => { };
  }

  const cleanedBaseKey = normalizeBaseKey(baseKey);
  if (!cleanedBaseKey) {
    callback([]);
    return () => { };
  }

  const entry = ensureSharedAppInstanceSubscription(cleanedBaseKey);
  entry.listeners.add(callback);
  callback(entry.rows);

  return () => {
    const current = sharedAppInstanceSubscriptions.get(entry.scopeKey);
    if (!current) return;

    current.listeners.delete(callback);
    if (current.listeners.size > 0) return;

    current.unsubscribe?.();
    sharedAppInstanceSubscriptions.delete(entry.scopeKey);
  };
}
